import type { RootState } from './RootReducer';
import type {
  PortfolioEntry,
  PortfolioTokenSecurity,
  PortfolioTokenTag,
  PortfolioWalletRequest,
} from '../api/models/PortfolioModels';
import type { AssetClass, PortfolioSummary } from '../pages/Portfolio/portfolio.types';

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/es/storage';
import { createSelectorCreator, defaultMemoize } from 'reselect';

import { getPortfolio, listPortfolioTags } from '../api/Portfolio';

/**
 * RTK 1.9's `createSelector` memoizes only the most recent argument set (cache
 * size 1). The parameterized selectors below are called once per list row with
 * a different key, so a size-1 cache would thrash — recomputing and returning a
 * new object reference on every store update, re-rendering every row. This LRU
 * variant caches many keys so each row's result stays referentially stable.
 */
const createCachedSelector = createSelectorCreator(defaultMemoize, { maxSize: 100 });

type Blockchain = string;
type Address = string;

export type TokenHash = `${Blockchain}-${string}-${Address}`;

export function createTokenHash(token: {
  blockchain: string;
  symbol: string;
  address?: string | null;
}): TokenHash {
  return `${token.blockchain.toLowerCase()}-${token.symbol.toLowerCase()}-${(
    token.address ?? ''
  ).toLowerCase()}`;
}

export interface StoredToken {
  blockchain: Blockchain;
  symbol: string;
  address: string | null;
  name?: string;
  logo: string;
  assetClass: string;
  accounts: {
    address: string;
    explorer: string;
    balance: { amount: number; decimals: number; usdPrice?: number };
  }[];
  security: PortfolioTokenSecurity;
}

export type TokenSymbol = string;
export type AssetId = number;

export type PortfolioState = {
  loading: boolean;
  error: string | null;
  /** Mask balances/values across the portfolio. Persisted via redux-persist (whitelisted). */
  hideBalance: boolean;
  tags: PortfolioTokenTag[];
  failedBlockchains: Blockchain[];
  _tokenMapByTokenHash: Record<TokenHash, StoredToken>;
  _tokenMapByBlockchainName: Record<
    Blockchain,
    { totalUsdValue: number; walletAddresses: string[]; tokens: TokenHash[] }
  >;
  _tokenMapByAsset: Record<
    AssetId | TokenSymbol,
    {
      /** The asset's own key, kept so buckets preserve its original type (object keys are always strings). */
      id: AssetId | TokenSymbol;
      price?: number;
      totalBalance: number;
      totalUsdValue: number;
      logo?: string;
      tokens: TokenHash[];
      /** Canonical class for the whole asset: a real class tag if any of its tokens has one, else 'OTHER'. */
      assetClass: string;
    }
  >;
  _assetMapByClass: Record<string, (AssetId | TokenSymbol)[]>;
};

const initialState: PortfolioState = {
  loading: true,
  error: null,
  hideBalance: false,
  tags: [],
  failedBlockchains: [],
  _tokenMapByTokenHash: {},
  _tokenMapByBlockchainName: {},
  _tokenMapByAsset: {},
  _assetMapByClass: {},
};

export const fetchPortfolioTagsAndDetails = createAsyncThunk<
  { tags: PortfolioTokenTag[]; portfolio: PortfolioEntry[] },
  { wallets: PortfolioWalletRequest[] },
  { rejectValue: string }
>('portfolio/fetchPortfolioTagsAndDetails', async (params, thunkApi) => {
  try {
    const [{ tags }, { portfolio }] = await Promise.all([
      listPortfolioTags(thunkApi.signal),
      getPortfolio(params.wallets, thunkApi.signal),
    ]);
    return { tags, portfolio };
  } catch (error) {
    if (thunkApi.signal.aborted) {
      // Superseded by a newer request; rethrow so RTK marks it aborted (not a real failure).
      throw error;
    }
    return thunkApi.rejectWithValue('Failed to fetch portfolio tags and details');
  }
});

function getUsdValueOf(balance: number, usdPrice?: number): number | undefined {
  return usdPrice != null ? balance * usdPrice : undefined;
}

function sumTokenBalance(token: StoredToken): number {
  return token.accounts.reduce((sum, account) => sum + account.balance.amount, 0);
}

function tokenUsdValue(token: StoredToken): number | undefined {
  return getUsdValueOf(sumTokenBalance(token), token.accounts[0].balance.usdPrice);
}

/**
 * Order by USD value, highest first. Entries without a USD value rank below those
 * that have one, and are ordered by balance (highest first) among themselves.
 */
function compareByValueThenBalance(
  aValue: number | undefined,
  aBalance: number,
  bValue: number | undefined,
  bBalance: number,
): number {
  if (aValue != null && bValue != null) {
    return bValue - aValue;
  }
  if (aValue != null) {
    return -1;
  }
  if (bValue != null) {
    return 1;
  }
  return bBalance - aBalance;
}

/** Push `value` onto `list` only if it isn't already present (keeps arrays duplicate-free, Set-free). */
function pushUnique<T>(list: T[], value: T): void {
  if (!list.includes(value)) {
    list.push(value);
  }
}

function clearIndexes(state: PortfolioState) {
  state.tags = [];
  state.failedBlockchains = [];
  state._tokenMapByTokenHash = {};
  state._tokenMapByBlockchainName = {};
  state._tokenMapByAsset = {};
  state._assetMapByClass = {};
}

export const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    /**
     * Clear fetched token data (e.g. all wallets disconnected); keep the hide-balance
     * preference. `loading: false` so the disconnected UI isn't stuck on the loading state
     * (no fetch runs after a reset).
     */
    resetPortfolio: (state) => ({
      ...initialState,
      hideBalance: state.hideBalance,
      loading: false,
    }),
    toggleHideBalance: (state) => {
      state.hideBalance = !state.hideBalance;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolioTagsAndDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolioTagsAndDetails.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch portfolio';
      })
      .addCase(fetchPortfolioTagsAndDetails.fulfilled, (state, action) => {
        const { tags, portfolio } = action.payload;

        clearIndexes(state);
        state.loading = false;
        state.error = null;
        state.tags = tags;

        for (const entry of portfolio) {
          const { blockchain } = entry.chain;

          if (entry.tokens === null) {
            if (!state.failedBlockchains.includes(blockchain)) {
              state.failedBlockchains.push(blockchain);
            }
            continue;
          }

          for (const token of entry.tokens) {
            const hash = createTokenHash({
              blockchain,
              symbol: token.symbol,
              address: token.address,
            });
            const balance = Number(token.balance);
            const usdValue = getUsdValueOf(balance, token.usdPrice) ?? 0;

            const assetTag = token.tags.find((tag) => tag.type === 'ASSET');
            const classTag = token.tags.find((tag) => tag.type === 'ASSET_CLASS');
            const asset = assetTag?.id ?? token.symbol;
            const assetClass = classTag?.value ?? 'OTHER';

            const account = {
              ...entry.account,
              balance: { amount: balance, decimals: token.decimals, usdPrice: token.usdPrice },
            };

            const existing = state._tokenMapByTokenHash[hash];
            if (existing) {
              // Same token in another wallet: aggregate, keep the wallet split.
              existing.accounts.push(account);
            } else {
              state._tokenMapByTokenHash[hash] = {
                blockchain,
                symbol: token.symbol,
                name: token.name,
                address: token.address,
                logo: token.logo,
                assetClass,
                accounts: [account],
                security: token.security,
              };
            }

            const blockchainEntry = (state._tokenMapByBlockchainName[blockchain] ??= {
              totalUsdValue: 0,
              walletAddresses: [],
              tokens: [],
            });
            blockchainEntry.totalUsdValue += usdValue;
            pushUnique(blockchainEntry.walletAddresses, entry.account.address);
            pushUnique(blockchainEntry.tokens, hash);
            const assetEntry = (state._tokenMapByAsset[asset] ??= {
              id: asset,
              tokens: [],
              totalBalance: 0,
              totalUsdValue: 0,
              logo: assetTag?.logo ?? token.logo,
              assetClass: 'OTHER',
            });
            pushUnique(assetEntry.tokens, hash);
            assetEntry.totalBalance += balance;
            assetEntry.totalUsdValue += usdValue;
            assetEntry.price ??= token.usdPrice;
            /*
             * An asset belongs to a single class. Prefer a real class tag over the
             * 'OTHER' fallback so an asset with a real class stays under it even when
             * some of its per-chain tokens are untagged (which would otherwise also
             * list it under Other). `_assetMapByClass` is built from this once all
             * tokens are aggregated.
             */
            if (assetClass !== 'OTHER' && assetEntry.assetClass === 'OTHER') {
              assetEntry.assetClass = assetClass;
            }
          }
        }

        // Bucket each asset under its single canonical class (built once all tokens are aggregated).
        for (const entry of Object.values(state._tokenMapByAsset)) {
          pushUnique((state._assetMapByClass[entry.assetClass] ??= []), entry.id);
        }
      });
  },
});

export const { toggleHideBalance, resetPortfolio } = portfolioSlice.actions;
const PortfolioReducer = portfolioSlice.reducer;
export const PersistedPortfolioReducer = persistReducer(
  {
    key: 'portfolio',
    storage,
    whitelist: ['hideBalance'],
  },
  PortfolioReducer,
);

export function selectHideValues(state: RootState): boolean {
  return state.portfolio.hideBalance;
}

export const selectTokenClasses = createSelector(
  [(state: RootState) => state.portfolio.tags],
  (tags) => tags.filter((tag) => tag.type === 'ASSET_CLASS'),
);

export const selectAssetsByClass = createCachedSelector(
  [
    (state: RootState) => state.portfolio._assetMapByClass,
    (state: RootState) => state.portfolio._tokenMapByAsset,
    (_state: RootState, filter: AssetClass) => filter,
  ],
  (assetMapByClass, tokensByAsset, filter) => {
    const assets =
      filter === 'ALL' ? Object.values(assetMapByClass).flat() : (assetMapByClass[filter] ?? []);
    /*
     * Dedupe first: an asset present in more than one class bucket would otherwise
     * yield duplicate React keys in the list.
     */
    return [...new Set(assets)].sort((a, b) => {
      const entryA = tokensByAsset[a];
      const entryB = tokensByAsset[b];
      // An asset's USD value is only meaningful once it has a price.
      const valueA = entryA?.price != null ? entryA.totalUsdValue : undefined;
      const valueB = entryB?.price != null ? entryB.totalUsdValue : undefined;
      return compareByValueThenBalance(
        valueA,
        entryA?.totalBalance ?? 0,
        valueB,
        entryB?.totalBalance ?? 0,
      );
    });
  },
);

export const selectPortfolioBlockchains = createSelector(
  [(state: RootState) => state.portfolio._tokenMapByBlockchainName],
  (tokensByBlockchains) => {
    return Object.keys(tokensByBlockchains).sort(
      (a, b) => tokensByBlockchains[b].totalUsdValue - tokensByBlockchains[a].totalUsdValue,
    );
  },
);

function hydrateTokens<T extends { tokens: TokenHash[] }>(
  data: T | undefined,
  tokensByTokenHash: Record<TokenHash, StoredToken>,
): (Omit<T, 'tokens'> & { tokens: StoredToken[] }) | undefined {
  if (!data) {
    return undefined;
  }
  const tokens = data.tokens
    .map((tokenHash) => tokensByTokenHash[tokenHash])
    .sort((a, b) =>
      compareByValueThenBalance(
        tokenUsdValue(a),
        sumTokenBalance(a),
        tokenUsdValue(b),
        sumTokenBalance(b),
      ),
    );
  return { ...data, tokens };
}

export const selectTokenByAsset = createCachedSelector(
  [
    (state: RootState) => state.portfolio._tokenMapByAsset,
    (state: RootState) => state.portfolio._tokenMapByTokenHash,
    (_state: RootState, asset: AssetId | TokenSymbol) => asset,
  ],
  (tokensByAsset, tokensByTokenHash, assetValue) =>
    hydrateTokens(tokensByAsset[assetValue], tokensByTokenHash),
);

export const selectTokenByBlockchain = createCachedSelector(
  [
    (state: RootState) => state.portfolio._tokenMapByBlockchainName,
    (state: RootState) => state.portfolio._tokenMapByTokenHash,
    (_state: RootState, blockchain: string) => blockchain,
  ],
  (tokensByBlockchain, tokensByTokenHash, blockchain) =>
    hydrateTokens(tokensByBlockchain[blockchain], tokensByTokenHash),
);

export const selectPortfolioSummary = createSelector(
  [
    (state: RootState) => state.portfolio._tokenMapByBlockchainName,
    (state: RootState) => state.portfolio._tokenMapByTokenHash,
    (state: RootState) => state.portfolio.failedBlockchains,
  ],
  /*
   * `walletCount` isn't derivable from portfolio data (it's a wallet-connection concern);
   * the consumer merges it in, so the selector only owns the token-derived metrics.
   */
  (
    tokensByBlockchain,
    tokensByTokenHash,
    failedBlockchains,
  ): Omit<PortfolioSummary, 'walletCount'> => {
    return {
      totalUsdValue: Object.values(tokensByBlockchain).reduce(
        (acc, item) => acc + item.totalUsdValue,
        0,
      ),
      isApprox: !!failedBlockchains.length,
      tokenCount: Object.keys(tokensByTokenHash).length,
      networkCount: Object.keys(tokensByBlockchain).length,
    };
  },
);
